import { useOptionalAuthContext } from '../contexts/AuthContext';
import { useAccountPlan } from './useAccountUsage';

/**
 * useRequiresPlan (task 467) — true only when the signed-in User's account
 * verifiably has NO plan, meaning every AI action is guaranteed to fail closed
 * server-side. The four AI entry points (Compose a service, UI Composer
 * Generate, test generation, endpoint Test/Run) render their primary action
 * disabled-with-reason on it.
 *
 * The plan comes from `GET /api/accounts/usage` (task 676 — previously the JWT
 * `plan` claim).
 *
 * Fail-open by design — this is a courtesy layer, server-side enforcement is
 * the truth (tasks 464/465 cover the bypass path). Returns false when:
 *   - there is no AuthProvider (component test renders),
 *   - AuthContext is still loading (no false lockouts during init),
 *   - the user has not resolved yet (token refresh in flight),
 *   - the plan lookup has not answered yet, or failed,
 *   - a plan is present.
 */
export function useRequiresPlan(): boolean {
  const auth = useOptionalAuthContext();
  // Hooks must run unconditionally — the guards below are all early *returns*,
  // never early exits before this call.
  const { plan, isResolved } = useAccountPlan();

  if (!auth) return false;
  if (auth.isLoading) return false;
  if (!auth.user) return false;
  if (!isResolved) return false;
  return !plan;
}
